import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getAsignaciones, updateAsignacion, bulkUpdateAsignacionesStatus as bulkUpdateAsignacionesStatusAPI } from '../../../shared/helpers/api/mantenedores/extras';
/**
 * Hook para manejar las asignaciones de extras
 * Gestiona el estado y lógica para las asignaciones a conductores
 */
export const useAsignacionesExtras = (user, permisos = {}) => {
  // Estados principales
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  // Estados para paginación y filtros
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [filtros, setFiltros] = useState({
    searchValue: '',
    usuario_movil_id: null,
    tipo_extra_id: null,
    extra_id: null,
    empresa_id: null,
    estado_id: null,
    fecha_inicio: null,
    fecha_fin: null
  });

  // Obtener permisos del usuario
  const permissionsState = useSelector(state => state.permissions);
  const userPermissions = permissionsState?.permissions;
  const extrasPermissions = userPermissions?.modules?.extras || {};

  /**
   * Cargar asignaciones desde la API
   */
  const loadAsignaciones = async (resetPage = false) => {
    if (!extrasPermissions.canView) return;
    
    // console.log('loadAsignaciones called with:', { resetPage, filtros, pageNumber });
    
    setLoading(true);
    setError(null);
    
    const currentPage = resetPage ? 1 : pageNumber;
    if (resetPage) setPageNumber(1);
    
    try {
      // Preparar parámetros de filtros - solo incluir los que tienen valor
      const params = {
        pageSize,
        pageNumber: currentPage
      };

      // Agregar filtros solo si tienen valor
      if (filtros.searchValue?.trim()) {
        params.searchValue = filtros.searchValue.trim();
      }
      if (filtros.usuario_movil_id) {
        params.usuario_movil_id = filtros.usuario_movil_id;
      }
      if (filtros.tipo_extra_id) {
        params.tipo_extra_id = filtros.tipo_extra_id;
      }
      if (filtros.extra_id) {
        params.extra_id = filtros.extra_id;
      }
      if (filtros.empresa_id) {
        params.empresa_id = filtros.empresa_id;
      }
      if (filtros.fecha_inicio) {
        params.fecha_inicio = filtros.fecha_inicio;
      }
      if (filtros.fecha_fin) {
        params.fecha_fin = filtros.fecha_fin;
      }
      if (filtros.estado_id) {
        params.estado_id = filtros.estado_id;
      }

      console.log('Fetching asignaciones with params:', params);

      const response = await getAsignaciones(user, params);
      
      if (response.success) {
        setAsignaciones(response.data);
        setTotal(response.total);
      } else {
        setError(response.error);
        setAsignaciones([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error al cargar asignaciones:', err);
      setError('Error al cargar asignaciones');
      setAsignaciones([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Cargar datos iniciales
   */
  useEffect(() => {
    // console.log('Hook useAsignacionesExtras - Effect triggered:', { user: !!user, canView: extrasPermissions.canView, pageNumber, filtros });
    if (user && extrasPermissions.canView) {
      loadAsignaciones();
    }
  }, [user, extrasPermissions.canView, pageNumber, filtros]);

  // Handlers para filtros
  const handleFiltersChange = (newFiltros) => {
    console.log('Filtros aplicados:', newFiltros);
    setFiltros(newFiltros);
    setPageNumber(1); // Reset a página 1 cuando cambien filtros
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const refreshAsignaciones = () => {
    loadAsignaciones();
  };

  // Utilidades para formateo
  const formatearMonto = (monto, tipoExtra) => {
    const valor = tipoExtra === 'bonos' ? Math.abs(monto) : -Math.abs(monto);
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(valor);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    // Espera formato "yyyy-mm-dd"
    const [year, month, day] = fecha.split('-');
    return `${day}-${month}-${year}`;
  };

  const getBadgeVariant = (tipoExtra) => {
    switch (tipoExtra?.toLowerCase()) {
      case 'bonos': return 'success';
      case 'descuento': return 'warning';
      case 'perdidas': return 'danger';
      default: return 'danger';
    }
  };

  const getStatusBadgeVariant = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'creado': return 'warning';
      case 'aprobado': return 'success';
      case 'rechazado': return 'danger';
      default: return 'danger';
    }
  };

   const actualizarAsignacion = async (asignacionId, data) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };
    return await updateAsignacion(user, asignacionId, data);
  };

  const bulkUpdateAsignacionesStatus = async (data) => {
    if (!user) return { success: false, error: 'Usuario no autenticado' };
    return await bulkUpdateAsignacionesStatusAPI(user, data);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    // Estados
    asignaciones,
    loading,
    error,
    total,
    
    // Paginación y filtros
    pageNumber,
    pageSize,
    totalPages,
    filtros,
    
    // Permisos
    permisos: extrasPermissions,
    
    // Funciones
    loadAsignaciones,
    refreshAsignaciones,
    handleFiltersChange,
    handlePageChange,
    
    // Utilidades
    formatearMonto,
    formatearFecha,
    getBadgeVariant,
    getStatusBadgeVariant,

    // Acciones
    actualizarAsignacion,
    bulkUpdateAsignacionesStatus
  };
};
